import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Field, Input, Select } from '@zt/ui';
import { Figure, Section, Table, td, tdR, th, thR, usePct } from '../components/reports/blocks';
import { api, type ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import type { Brand, Category, PriceList } from '../lib/types';
import type { PriceSimulation } from '../lib/types-faz8';
import { percentToBps } from './pricing';

/**
 * Fiyat simülasyonu ve komisyon sonrası kârlılık (F8-12, ADR-812). Hiçbir fiyatı değiştirmez; uygulamak için toplu
 * fiyat düzenleme (Fiyatlar sayfası) kullanılır. Rehber: docs/faz-8/REPORTS_FINANCE_TR.md#fiyat-simulasyonu.
 */
export function PriceSimulationPage() {
  const t = useT();
  const money = useMoney();
  const pct = usePct();
  const { formatNumber, formatDate } = useI18n();
  const lists = useQuery({
    queryKey: ['price-lists'],
    queryFn: () => api.get<PriceList[]>('/v1/admin/price-lists'),
  });
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
  });
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => api.get<Brand[]>('/v1/admin/brands') });
  const tryLists = (lists.data ?? []).filter((l) => l.currency === 'TRY');
  const [listId, setListId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [days, setDays] = useState<30 | 90 | 365>(90);
  const [price, setPrice] = useState('0');
  const [cost, setCost] = useState('0');
  const [fee, setFee] = useState('');
  const [minMargin, setMinMargin] = useState('');
  const effectiveList = listId || tryLists[0]?.id || '';
  const priceBps = percentToBps(price);
  const costBps = percentToBps(cost);
  const feeBps = fee.trim() ? percentToBps(fee) : null;
  const minBps = minMargin.trim() ? percentToBps(minMargin) : null;
  const invalid =
    priceBps === null ||
    costBps === null ||
    (fee.trim() !== '' && (feeBps === null || feeBps < 0)) ||
    (minMargin.trim() !== '' && minBps === null);
  const run = useMutation<PriceSimulation, ApiError>({
    mutationFn: () =>
      api.post<PriceSimulation>('/v1/admin/reports/price-simulation', {
        priceListId: effectiveList,
        ...(categoryId ? { categoryId } : {}),
        ...(brandId ? { brandId } : {}),
        days,
        priceChangeBps: priceBps,
        costChangeBps: costBps,
        feeRateBps: feeBps,
        minMarginBps: minBps,
      }),
  });
  const r = run.data;
  const signed = (n: number) => (n > 0 ? `+${money(n, 'TRY')}` : money(n, 'TRY'));
  return (
    <PageFrame helpKey="page.price_simulation" wide>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t('admin.priceSimulation.priceList')} required>
          {(p) => (
            <Select {...p} value={effectiveList} onChange={(e) => setListId(e.target.value)}>
              {tryLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.priceSimulation.category')}>
          {(p) => (
            <Select {...p} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">{t('admin.reports.all')}</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.priceSimulation.brand')}>
          {(p) => (
            <Select {...p} value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">{t('admin.reports.all')}</option>
              {(brands.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.priceSimulation.days')} help={<HelpTip k="price_simulation.volume" />}>
          {(p) => (
            <Select {...p} value={days} onChange={(e) => setDays(Number(e.target.value) as 30 | 90 | 365)}>
              {([30, 90, 365] as const).map((d) => (
                <option key={d} value={d}>
                  {t('admin.priceSimulation.lastDays', { count: d })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.priceSimulation.priceChange')}
          help={<HelpTip k="price_simulation.scenario" />}
          error={priceBps === null ? t('admin.priceSimulation.percentInvalid') : undefined}
        >
          {(p) => (
            <Input {...p} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
          )}
        </Field>
        <Field
          label={t('admin.priceSimulation.costChange')}
          error={costBps === null ? t('admin.priceSimulation.percentInvalid') : undefined}
        >
          {(p) => <Input {...p} inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.priceSimulation.feeRate')}
          optionalLabel={t('admin.priceSimulation.feeDefault')}
          help={<HelpTip k="price_simulation.fee" />}
          error={
            fee.trim() && (feeBps === null || feeBps < 0)
              ? t('admin.priceSimulation.percentInvalid')
              : undefined
          }
        >
          {(p) => <Input {...p} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.priceSimulation.minMargin')}
          optionalLabel={t('common.optional')}
          error={minMargin.trim() && minBps === null ? t('admin.priceSimulation.percentInvalid') : undefined}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={minMargin}
              onChange={(e) => setMinMargin(e.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          disabled={!effectiveList || invalid}
          loading={run.isPending}
          onClick={() => run.mutate()}
        >
          {t('admin.priceSimulation.run')}
        </Button>
        <p className="text-sm text-ink-3">{t('admin.priceSimulation.noWrite')}</p>
      </div>
      {lists.data && !tryLists.length && <EmptyState title={t('admin.priceSimulation.noLists')} />}
      {run.isError && (
        <p role="alert" className="text-sm text-rose">
          {run.error.title}
        </p>
      )}
      {r && (
        <>
          <section
            aria-label={t('admin.priceSimulation.summary')}
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            <Figure
              label={t('admin.priceSimulation.currentContribution')}
              value={r.unitsVisible ? money(r.totals.currentContribution, 'TRY') : '—'}
              sub={t('admin.priceSimulation.periodOf', {
                from: formatDate(r.period.from, false),
                to: formatDate(r.period.to, false),
              })}
            />
            <Figure
              label={t('admin.priceSimulation.scenarioContribution')}
              value={r.unitsVisible ? money(r.totals.scenarioContribution, 'TRY') : '—'}
              sub={
                r.unitsVisible
                  ? t('admin.priceSimulation.changeOf', { amount: signed(r.totals.change) })
                  : undefined
              }
            />
            <Figure
              label={t('admin.priceSimulation.feeUsed')}
              value={pct(r.scenario.feeRateBps)}
              sub={
                r.fee.currentBps === null
                  ? t('admin.priceSimulation.feeNone')
                  : t('admin.priceSimulation.feeCurrent', {
                      pct: pct(r.fee.currentBps),
                      count: formatNumber(r.fee.payments, 0),
                    })
              }
            />
            <Figure
              label={t('admin.priceSimulation.warnings')}
              value={formatNumber(r.negative + r.lowMargin, 0)}
              sub={t('admin.priceSimulation.warningsOf', {
                negative: formatNumber(r.negative, 0),
                low: formatNumber(r.lowMargin, 0),
              })}
            />
          </section>
          <div className="space-y-1 text-xs text-ink-3">
            <p>{t('admin.priceSimulation.assumption')}</p>
            {r.noCost > 0 && <p>{t('admin.priceSimulation.noCost', { count: formatNumber(r.noCost, 0) })}</p>}
            {r.fee.unknownPayments > 0 && (
              <p className="text-amber">
                {t('admin.priceSimulation.feeUnknown', { count: formatNumber(r.fee.unknownPayments, 0) })}
              </p>
            )}
            {!r.unitsVisible && <p>{t('admin.priceSimulation.unitsHidden')}</p>}
            {r.truncated && <p>{t('admin.priceSimulation.truncated', { count: r.rows.length })}</p>}
          </div>
          <Section title={t('admin.priceSimulation.tableTitle')}>
            {!r.rows.length ? (
              <EmptyState title={t('admin.priceSimulation.empty')} />
            ) : (
              <Table
                caption={t('admin.priceSimulation.tableTitle')}
                head={
                  <tr>
                    <th scope="col" className={th}>
                      {t('admin.priceSimulation.cols.product')}
                    </th>
                    {(['units', 'price', 'net', 'cost', 'fee', 'contribution', 'margin'] as const).map(
                      (c) => (
                        <th key={c} scope="col" className={thR}>
                          {t(`admin.priceSimulation.cols.${c}`)}
                        </th>
                      ),
                    )}
                  </tr>
                }
              >
                {r.rows.map((x) => {
                  const pair = (a: number, b: number) =>
                    a === b ? money(a, 'TRY') : `${money(a, 'TRY')} → ${money(b, 'TRY')}`;
                  return (
                    <tr key={x.variantId} className="border-t border-line">
                      <th scope="row" className={`${td} text-start font-normal`}>
                        <span className="font-medium text-ink">{x.label}</span>
                        <span className="ms-2 text-xs text-ink-3">{x.sku}</span>
                        {x.negative && (
                          <Badge tone="rose" className="ms-2">
                            {t('admin.priceSimulation.loss')}
                          </Badge>
                        )}
                        {!x.negative && x.lowMargin && (
                          <Badge tone="amber" className="ms-2">
                            {t('admin.priceSimulation.low')}
                          </Badge>
                        )}
                      </th>
                      <td className={tdR}>{x.units === null ? '—' : formatNumber(x.units, 0)}</td>
                      <td className={tdR}>{pair(x.current.price, x.scenario.price)}</td>
                      <td className={tdR}>{pair(x.current.net, x.scenario.net)}</td>
                      <td className={tdR}>{pair(x.current.cost, x.scenario.cost)}</td>
                      <td className={tdR}>{pair(x.current.fee, x.scenario.fee)}</td>
                      <td className={tdR}>{pair(x.current.contribution, x.scenario.contribution)}</td>
                      <td className={tdR}>
                        {x.current.marginBps === x.scenario.marginBps
                          ? pct(x.current.marginBps)
                          : `${pct(x.current.marginBps)} → ${pct(x.scenario.marginBps)}`}
                      </td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Section>
          <p className="text-sm">
            <Link to="/fiyatlar" className="text-pine hover:underline">
              {t('admin.priceSimulation.goBulk')}
            </Link>
          </p>
        </>
      )}
    </PageFrame>
  );
}
